import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { getFileRules, postFile, type FileRulesType } from 'utils/apiList/files'

import {
  HomeTabCreateEditFormType,
  HomeTabImageType,
} from 'features/MultiCategory/@types/homeTabCreateEdit'
import {
  HOME_TAB_IMAGE_TYPE,
  HOME_TAB_ICON_FILE_VARIANT_ID,
} from 'features/MultiCategory/constant/homeTabCreateEdit'
import { validateIconFile } from 'features/MultiCategory/utils/file'
import { HomeTabImagePayloadType } from 'features/MultiCategory/services/homeTabCreateEdit'
import { SLICE_NAME, setIconFileRule } from './slice'

type IconUploadItemType = {
  imageType: number
  file: File | null
  existingUrl: string
}

const getIconUploadItems = (form: HomeTabCreateEditFormType): IconUploadItemType[] => [
  {
    imageType: HOME_TAB_IMAGE_TYPE.SELECTED,
    file: form.iconSelected,
    existingUrl: form.iconSelectedUrl,
  },
  {
    imageType: HOME_TAB_IMAGE_TYPE.UNSELECTED,
    file: form.iconUnselected,
    existingUrl: form.iconUnselectedUrl,
  },
]

/**
 * Reuses the stored record for an untouched icon so update keeps all three
 * resolutions — the form only carries the medium URL for preview.
 */
const toExistingImagePayload = (
  { imageType, existingUrl }: IconUploadItemType,
  storedImages: HomeTabImageType[] = [],
): HomeTabImagePayloadType => {
  const stored = storedImages.find((image) => image.imageType === imageType)

  return {
    image_type: imageType,
    url_small: stored?.urlSmall || existingUrl,
    url_medium: stored?.urlMedium || existingUrl,
    url_large: stored?.urlLarge || existingUrl,
  }
}

/** Rejects the file before upload when it breaks the variant's file rules. */
const assertFileMatchesRules = async (file: File, fileRules: FileRulesType) => {
  const errorMessage = await validateIconFile(file, fileRules)

  if (errorMessage) {
    throw new Error(`Icon ${file.name}: ${errorMessage}`)
  }
}

/**
 * The file service stores the icon as-is and returns a single URL, so all three
 * resolutions the tab API expects point at that same file.
 */
const uploadIcon = async (file: File, fileRules: FileRulesType) => {
  await assertFileMatchesRules(file, fileRules)

  const {
    data: {
      data: { fileUrl },
    },
  } = await postFile({ typeId: HOME_TAB_ICON_FILE_VARIANT_ID, data: { file_input: file } })

  return {
    url_small: fileUrl,
    url_medium: fileUrl,
    url_large: fileUrl,
  }
}

/** Falls back to a fetch when the tab is submitted before the rules land. */
export const resolveIconFileRule = async (iconFileRule: Nullable<FileRulesType>) =>
  iconFileRule || (await getFileRules(HOME_TAB_ICON_FILE_VARIANT_ID)).data.data

/**
 * Uploads newly picked icons and keeps the ones already stored, returning the
 * `images` payload the API requires (at least one entry).
 */
export const uploadHomeTabIcons = async (
  form: HomeTabCreateEditFormType,
  fileRules: FileRulesType,
  /** Images already stored on the tab — kept for icons the user did not replace. */
  storedImages: HomeTabImageType[] = [],
) => {
  const items = getIconUploadItems(form).filter(({ file, existingUrl }) => file || existingUrl)

  if (!items.some(({ file }) => file)) {
    return items.map((item) => toExistingImagePayload(item, storedImages))
  }

  return Promise.all(
    items.map<Promise<HomeTabImagePayloadType>>(async (item) => {
      if (!item.file) {
        return toExistingImagePayload(item, storedImages)
      }

      return {
        image_type: item.imageType,
        ...(await uploadIcon(item.file, fileRules)),
      }
    }),
  )
}

export const fetchHomeTabIconFileRule = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabIconFileRule`,
  async (_: void, { dispatch, getState }) => {
    const {
      multiCategoryHomeTabCreateEdit: { iconFileRule },
    } = getState() as StoreStateType

    /* Master data — fetch once per page visit. */
    if (iconFileRule) {
      return
    }

    try {
      const {
        data: { data },
      } = await getFileRules(HOME_TAB_ICON_FILE_VARIANT_ID)

      dispatch(setIconFileRule(data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)
